import { Plus, Workflow, MoreHorizontal, Pencil, Trash2, Lock } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { getTriggerLabel } from '@/features/automations/triggerLabels'
import { useAutomations } from '@/hooks/useAutomations'
import { usePermission, useAllowedActionsBatch } from '@/hooks/usePermission'
import { useRecordGroups } from '@/hooks/useRecordGroups'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import { useSubscriptionStore } from '@/stores/subscription.store'
import { ActionType } from '@/types/enums'

export function AutomationsPage() {
  const navigate = useNavigate()
  const { automations, isLoading, toggle, delete: deleteAutomation, isToggling, isDeleting } = useAutomations()
  const { recordGroups } = useRecordGroups()
  const { lockedResourceIds } = useSubscriptionStore()
  const { canAnywhere } = usePermission()
  const canCreate = canAnywhere(PERMISSION_KEYS.AUTOMATION_CREATE)
  const automationActions = useAllowedActionsBatch('AUTOMATION', useMemo(() => automations.map((a) => a.id), [automations]))

  const getRecordGroupName = (id: string) => {
    return recordGroups.find((rg) => rg.id === id)?.name ?? 'Unknown'
  }

  const isRecordGroupLocked = (recordGroupId: string) =>
    lockedResourceIds.has(recordGroupId)

  const getTriggerBadgeVariant = (trigger: string) => {
    switch (trigger) {
      case ActionType.CREATE: return 'success'
      case ActionType.UPDATE: return 'default'
      case ActionType.DELETE: return 'destructive'
      default:                return 'secondary'
    }
  }

  const handleToggle = (id: string, currentEnabled: boolean, locked: boolean) => {
    if (locked) {
      toast.warning('This automation is locked. Upgrade your subscription to enable it.')
      return
    }
    toggle(
      { id, enabled: !currentEnabled },
      { onError: () => toast.error('Failed to update automation') }
    )
  }

  const handleDelete = (id: string) => {
    deleteAutomation(id, {
      onSuccess: () => toast.success('Automation moved to trash'),
      onError:   () => toast.error('Failed to delete automation'),
    })
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Automations</h1>
            <p className="mt-1 text-neutral-500">Automate actions on record events</p>
          </div>
          {canCreate && (
            <Button asChild>
              <Link to="/automations/new">
                <Plus className="mr-2 h-4 w-4" />
                New Automation
              </Link>
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-3">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </CardHeader>
                <CardContent><Skeleton className="h-4 w-full" /></CardContent>
              </Card>
            ))}
          </div>
        ) : automations.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Workflow className="h-12 w-12 text-neutral-300" />
              <h3 className="mt-4 text-lg font-medium">No automations yet</h3>
              <p className="mt-1 text-sm text-neutral-500">
                Create your first automation to automate record events
              </p>
              {canCreate && (
                <Button className="mt-4" asChild>
                  <Link to="/automations/new">
                    <Plus className="mr-2 h-4 w-4" />
                    Create Automation
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-3">
            {automations.map((automation) => {
              const locked = isRecordGroupLocked(automation.recordGroupId)

              return (
                <Card
                  key={automation.id}
                  className={`relative cursor-pointer transition-shadow hover:shadow-md
                    ${locked ? 'opacity-75 border-amber-200 bg-amber-50/30' : ''}`}
                  onClick={() => navigate(`/automations/${automation.id}`)}
                >
                  {/* Locked banner */}
                  {locked && (
                    <div className="absolute inset-x-0 top-0 flex items-center gap-1.5 rounded-t-lg bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
                      <Lock className="h-3 w-3" />
                      Record group locked — automation disabled
                    </div>
                  )}

                  <CardHeader className={`pb-2 ${locked ? 'pt-8' : ''}`}>
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <CardTitle className="flex items-center gap-2">
                          {getRecordGroupName(automation.recordGroupId)}
                          {locked && <Lock className="h-3.5 w-3.5 text-amber-500" />}
                        </CardTitle>
                        <CardDescription>
                          {automation.actions.length} action{automation.actions.length !== 1 ? 's' : ''}
                        </CardDescription>
                      </div>

                      <div
                        className="flex items-center gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Toggle — wrapped in tooltip when locked */}
                        {locked ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Switch checked={false} disabled className="cursor-not-allowed" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              Unlock the record group to enable this automation
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Switch
                            checked={automation.enabled}
                            onCheckedChange={() => handleToggle(automation.id, automation.enabled, false)}
                            disabled={isToggling || !automationActions.can(automation.id, PERMISSION_KEYS.AUTOMATION_TOGGLE)}
                          />
                        )}

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/automations/${automation.id}`}>
                                <Pencil className="mr-2 h-4 w-4" />
                                View Details
                              </Link>
                            </DropdownMenuItem>
                            {automationActions.can(automation.id, PERMISSION_KEYS.AUTOMATION_DELETE) && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <DropdownMenuItem
                                  className="text-destructive-600"
                                  onSelect={(e) => e.preventDefault()}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Move to Trash
                                </DropdownMenuItem>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Move to Trash?</DialogTitle>
                                  <DialogDescription>
                                    This automation will be moved to trash and can be restored within 30 days.
                                  </DialogDescription>
                                </DialogHeader>
                                <DialogFooter>
                                  <Button variant="outline">Cancel</Button>
                                  <Button
                                    variant="destructive"
                                    onClick={() => handleDelete(automation.id)}
                                    disabled={isDeleting}
                                  >
                                    {isDeleting ? 'Moving…' : 'Move to Trash'}
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant={getTriggerBadgeVariant(automation.triggerEvent)}>
                        {getTriggerLabel(automation.triggerEvent)}
                      </Badge>
                      {automation.actions.map((action, index) => (
                        <Badge key={index} variant="outline">{action.type}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}
